import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { usePeopleSearch } from "../peopleSearch";
import { useSession } from "../hooks";
import { draftKey, loadDraft, saveDraft } from "../drafts";
import { applyFormat, insertLink, type Format } from "../format";
import { sortedMessages, sortedReplies } from "../sync/store";
import { emojiQuery, groupCandidates, mentionCandidates, mentionQuery } from "../mentions";
import { Avatar, Form, Sheet } from "./ui";
import { modKey } from "./Shortcuts";
import { IconAttach, IconClock, IconClose, IconCode, IconCodeBlock, IconLink, IconList, IconNumbered, IconQuote, IconReaction, IconSend, IconSparkle } from "./icons";
import { TimePicker } from "./TimePicker";
import { whenLabel } from "../reminders";
import { builtIns, parseRemind, parseStatus } from "../commands";
import { navigate } from "../router";
import { EmojiPicker } from "./EmojiPicker";
import { Emoji } from "./Emoji";
import { glyph, isCustom, search as searchEmoji, noteUsed } from "../emoji";
import { list, t, tn, tx } from "../i18n";

type Command = { command: string; app_name: string; description: string; usage_hint: string };

const NewTaskSheet = lazy(() => import("./NewTaskSheet").then((m) => ({ default: m.NewTaskSheet })));

const mod = modKey;

// The formatting buttons: what each does, its name, how it is drawn, and
// whether it is one of the extras a phone leaves out.
const formats = (): [Format, string, ReactNode, boolean][] => [
  ["bold", t("Bold ({mod}B)", { mod }), <b key="b">B</b>, false],
  ["italic", t("Italic ({mod}I)", { mod }), <i key="i" style={{ fontFamily: "Georgia, serif" }}>I</i>, false],
  ["strike", t("Strikethrough ({mod}Shift+X)", { mod }), <s key="s">S</s>, false],
  ["code", t("Code"), <IconCode key="c" />, false],
  ["block", t("Code block"), <IconCodeBlock key="cb" />, true],
  ["quote", t("Quote"), <IconQuote key="q" />, true],
  ["list", t("Bulleted list"), <IconList key="l" />, true],
  ["numbered", t("Numbered list"), <IconNumbered key="n" />, true],
];

export function Composer({ channel, threadRoot, placeholder }: { channel: string; threadRoot?: string; placeholder: string }) {
  const session = useSession();
  // What is typed is kept as a draft for this channel or thread until sent.
  const key = draftKey(session.store.getSnapshot().me?.id ?? "", channel, threadRoot);
  const [text, setText] = useState(() => loadDraft(key));
  useEffect(() => {
    saveDraft(key, text);
    if (threadRoot) return;
    const has = text.trim() !== "";
    if (session.store.getSnapshot().drafts.has(channel) !== has)
      session.store.update((s) => {
        if (has) s.drafts.add(channel);
        else s.drafts.delete(channel);
      });
  }, [key, text]);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const [commands, setCommands] = useState<Command[]>([]);
  // An app's answer only the sender sees.
  const [ephemeral, setEphemeral] = useState("");
  // asking is what "/task" named, while its sheet is open.
  const [asking, setAsking] = useState<string | null>(null);

  // @mentions: the query being typed, the channel's members (listed first)
  // and which suggestion the arrow keys are on.
  const box = useRef<HTMLTextAreaElement>(null);
  // Re-edit on a message one recalled here puts its text back in this box,
  // after what is already typed.
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ channel: string; thread: string | null; text: string }>).detail;
      if (d.channel !== channel || d.thread !== (threadRoot ?? null)) return;
      e.preventDefault();
      setText((now) => (now.trim() ? now.replace(/\s*$/, "\n") + d.text : d.text));
      box.current?.focus();
    };
    addEventListener("lantern:reedit", on);
    return () => removeEventListener("lantern:reedit", on);
  }, [channel, threadRoot]);
  const [caret, setCaret] = useState(0);
  const [members, setMembers] = useState<Set<string>>(new Set());
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState(-1);
  useEffect(() => {
    let live = true;
    session.api.members(channel).then(
      (r) => live && setMembers(new Set(r.members)),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [session, channel]);
  const allGroups = useSyncExternalStore(session.store.subscribe, () => session.store.getSnapshot().groups);
  const scheduled = useSyncExternalStore(session.store.subscribe, () => session.store.getSnapshot().scheduled);
  const [scheduling, setScheduling] = useState<DOMRect | null>(null);
  const mq = mentionQuery(text, caret);
  // In a large workspace the server finds who matches.
  const found = usePeopleSearch(mq && mq.start !== dismissed ? mq.query : null);
  const pool = found === null ? session.directory.values() : mq?.query ? found : [...members].flatMap((id) => session.directory.get(id) ?? []);
  const people = mq && mq.start !== dismissed ? mentionCandidates(pool, members, session.store.getSnapshot().me?.id, mq.query) : [];
  const groups = mq && mq.start !== dismissed ? groupCandidates(allGroups, mq.query) : [];
  // Groups change rarely and are not pushed: the @ list fetches them again.
  const picking = !!mq && mq.start !== dismissed;
  useEffect(() => {
    if (picking) void session.loadGroups();
  }, [picking, session]);
  // Typing :pa offers emoji.
  const eq = mq ? null : emojiQuery(text, caret);
  const emojis = eq && eq.start !== dismissed ? searchEmoji(eq.query, 8) : [];
  // Everything the @ or : list offers, in its order, for the arrow keys.
  const choices = mq ? [...people.map((p) => p.handle), ...groups.map((g) => g.handle)] : emojis.map((e) => e.name);
  const open = mq ?? eq;

  function pick(name: string) {
    if (!open) return;
    const put = mq ? "@" + name + " " : ":" + name + ": ";
    if (!mq) noteUsed(name);
    const next = text.slice(0, open.start) + put + text.slice(caret);
    const at = open.start + put.length;
    setText(next);
    setActive(0);
    requestAnimationFrame(() => {
      box.current?.focus();
      box.current?.setSelectionRange(at, at);
      setCaret(at);
    });
  }

  // The box grows with what is typed, up to a limit, then scrolls.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 240) + "px";
  }, [text]);

  // Files come from the picker, a paste, or a drop on the box.
  const [dropping, setDropping] = useState(false);
  const addFiles = (list: FileList | null | undefined) => {
    const more = Array.from(list ?? []);
    if (more.length) setFiles((f) => [...f, ...more]);
  };
  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDropping(false);
    addFiles(e.dataTransfer.files);
    box.current?.focus();
  }

  const [emojiAt, setEmojiAt] = useState<DOMRect | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  const [helping, setHelping] = useState(false);
  useEffect(() => {
    void session.commands().then(setCommands);
    void session.features().then((f) => setCanWrite(!!f.large));
  }, [session]);

  // help asks the model to draft, rewrite or translate; the person reviews
  // the result in the box before sending.
  async function help(choice: string) {
    if (!choice) return;
    let req: Parameters<typeof session.api.compose>[0] = { action: "rewrite", text, channel_id: channel, thread_root_id: threadRoot ?? null };
    if (choice === "draft") req = { ...req, action: "draft" };
    else if (choice === "translate") {
      const language = window.prompt(t("Translate into which language?"));
      if (!language) return;
      req = { ...req, action: "translate", language };
    } else req = { ...req, tone: choice };
    setHelping(true);
    setError("");
    try {
      setText((await session.api.compose(req)).text);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setHelping(false);
    }
  }

  const typed = text.startsWith("/") && !text.includes(" ") ? text.toLowerCase() : "";
  const offered: Command[] = [...builtIns().map((b) => ({ ...b, app_name: "Lantern" })), ...commands.filter((c) => !builtIns().some((b) => b.command === c.command))];
  const suggestions = typed ? offered.filter((c) => c.command.startsWith(typed)).slice(0, 6) : [];

  // runCommand sends "/command text" to the app that offers it.
  async function runCommand(line: string) {
    const [command, ...rest] = line.split(" ");
    if (builtIns().some((b) => b.command === command.toLowerCase())) {
      setEphemeral("");
      try {
        const said = await runBuiltIn(command.toLowerCase(), rest.join(" ").trim());
        if (said !== false) setText("");
        if (typeof said === "string") setEphemeral(said);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
      return true;
    }
    if (!commands.some((c) => c.command === command.toLowerCase())) return false;
    setText("");
    setEphemeral("");
    try {
      const reply = await session.api.runCommand(channel, command, rest.join(" "), threadRoot ?? null);
      if (!reply.posted && reply.text) setEphemeral(reply.text);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    return true;
  }

  // runBuiltIn runs one of Lantern's own commands, and says what it did; false
  // leaves what was typed, to correct it.
  async function runBuiltIn(command: string, args: string): Promise<string | boolean> {
    const post = (body: string) => session.engine.send(channel, session.mentionsIn(body), { threadRootID: threadRoot ?? null });
    const me = session.store.getSnapshot().me!;
    const cs = session.store.getSnapshot().channels.get(channel);
    switch (command) {
      case "/shrug":
        await post(`${args} ¯\\_(ツ)_/¯`.trim());
        return true;
      case "/me":
        if (!args) return t("Say what you are doing, as /me waves.");
        await post(`_${args}_`);
        return true;
      case "/topic":
        await session.api.updateChannel(channel, { topic: args });
        return args ? t("Topic set.") : t("Topic cleared.");
      case "/away":
        await session.setOwn(() => session.api.setAway(!me.away));
        return me.away ? t("You are set active.") : t("You are set away.");
      case "/status": {
        const st = parseStatus(args);
        await session.setOwn(() => session.api.setStatus(st.emoji ? glyph(st.emoji) : st.text ? "💬" : "", st.text, null));
        return st.text || st.emoji ? t("Status set.") : t("Status cleared.");
      }
      case "/mute": {
        const muted = cs?.channel.membership?.notify_level === "none";
        await session.api.setNotifyLevel(channel, muted ? "mentions" : "none");
        session.store.update((s) => {
          const m = s.channels.get(channel)?.channel.membership;
          if (m) m.notify_level = muted ? "mentions" : "none";
        });
        return muted ? t("Unmuted: you hear about mentions here again.") : t("Muted: this channel will not notify you.");
      }
      case "/leave":
        await session.api.removeMember(channel, me.id);
        session.store.update((s) => s.channels.delete(channel));
        navigate("/");
        return true;
      case "/invite": {
        const handles = [...args.matchAll(/@?([a-z0-9][a-z0-9._-]*)/gi)].map((m) => m[1].toLowerCase());
        const people = handles.map((h) => [...session.directory.values()].find((p) => p.handle === h));
        if (!handles.length || people.some((p) => !p)) return false;
        for (const p of people) await session.api.addMember(channel, p!.id);
        setMembers((m) => new Set([...m, ...people.map((p) => p!.id)]));
        return t("Added {names} to the channel.", { names: list(people.map((p) => session.name(p!.id))) });
      }
      case "/remind": {
        const r = parseRemind(args);
        if (!r) return t("Say what and when, as /remind me to check the deploy in 20 minutes, or tomorrow at 9.");
        await session.api.createReminder({ text: r.what, remind_at: r.at.toISOString() });
        await session.loadReminders();
        return t("I will remind you {when}: {what}", { when: whenLabel(r.at), what: r.what });
      }
      case "/task":
        setAsking(args);
        return true;
    }
    return false;
  }

  // schedule keeps what is typed to send at a time.
  async function schedule(at: Date) {
    const body = text.trim();
    if (!body) return;
    setError("");
    try {
      await session.api.scheduleMessage(channel, { text: session.mentionsIn(body), thread_root_id: threadRoot ?? null, send_at: at.toISOString() });
      setText("");
      await session.loadScheduled();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  // send sends what is typed. An agent hears only of mentions where it is a
  // member, so mentioned agents who are not are asked about first: added,
  // or left out (choice).
  async function send(choice?: "add" | "without") {
    const body = text.trim();
    if (!body && files.length === 0) return;
    setError("");
    if (body.startsWith("/") && files.length === 0 && (await runCommand(body))) return;
    const out = members.size > 0 ? mentionedIn(session.mentionsIn(body)).filter((id) => !members.has(id) && session.directory.get(id)?.kind === "agent") : [];
    if (!choice && out.length > 0) {
      setAgentsOut(out);
      return;
    }
    setAgentsOut([]);
    if (choice === "add" && !(await addAgents(out))) return;
    let fileIDs: string[] = [];
    if (files.length > 0) {
      setUploading(true);
      try {
        fileIDs = (await Promise.all(files.map((f) => session.api.upload(f, f.name)))).map((r) => r.file.id);
      } catch {
        setError(t("A file could not be uploaded."));
        setUploading(false);
        return;
      }
      setUploading(false);
    }
    setText("");
    setFiles([]);
    const sent = session.mentionsIn(body);
    void session.engine.send(channel, sent, { threadRootID: threadRoot ?? null, fileIDs, alsoInChannel: !!threadRoot && alsoInChannel });
    setAlsoInChannel(false);
    // Those mentioned who are not in the channel are not told; offer to add them.
    const cs = session.store.getSnapshot().channels.get(channel);
    const dm = cs?.channel.kind === "dm" || cs?.channel.kind === "group_dm";
    const missing = mentionedIn(sent).filter((id) => !members.has(id) && session.directory.get(id)?.kind === "user");
    if (!dm && members.size > 0) setOutside(missing);
  }

  const dmChannel = ["dm", "group_dm"].includes(session.store.getSnapshot().channels.get(channel)?.channel.kind ?? "");
  // alsoInChannel sends a thread reply to the channel too.
  const [alsoInChannel, setAlsoInChannel] = useState(false);

  // agentsOut are agents mentioned in what is about to be sent who are not
  // in the channel or conversation.
  const [agentsOut, setAgentsOut] = useState<string[]>([]);
  // addAgents adds them, each on its own: those refused stay asked about,
  // with why.
  async function addAgents(ids: string[]) {
    const added: string[] = [];
    const refused: string[] = [];
    let why = "";
    for (const id of ids) {
      try {
        await session.api.addMember(channel, id);
        added.push(id);
      } catch (err) {
        refused.push(id);
        why ||= t("Could not add {name}: {reason}", { name: session.name(id), reason: err instanceof Error ? err.message : String(err) });
      }
    }
    if (added.length) setMembers((m) => new Set([...m, ...added]));
    if (!refused.length) return true;
    setAgentsOut(refused);
    setError(why);
    return false;
  }

  // outside are people just mentioned who are not in the channel.
  const [outside, setOutside] = useState<string[]>([]);
  async function addOutside() {
    const ids = outside;
    setOutside([]);
    try {
      for (const id of ids) await session.api.addMember(channel, id);
      setMembers((m) => new Set([...m, ...ids]));
      setEphemeral(t("Added {names} to the channel.", { names: list(ids.map((id) => session.name(id))) }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  // linking is the selection a link is being made of, while its sheet is open.
  const [linking, setLinking] = useState<{ start: number; end: number; label: string } | null>(null);
  function startLink() {
    const el = box.current;
    if (!el) return;
    setLinking({ start: el.selectionStart, end: el.selectionEnd, label: text.slice(el.selectionStart, el.selectionEnd) });
  }

  // format applies a formatting button or shortcut to the selection.
  function format(f: Format) {
    const el = box.current;
    if (!el) return;
    const r = applyFormat(text, el.selectionStart, el.selectionEnd, f);
    selectAfter.current = r;
    setText(r.text);
    setCaret(r.end);
  }
  // The selection a format leaves, set as soon as its text is on screen so
  // the next key lands inside it.
  const selectAfter = useRef<{ start: number; end: number } | null>(null);
  useLayoutEffect(() => {
    const r = selectAfter.current;
    if (!r || !box.current) return;
    selectAfter.current = null;
    box.current.focus();
    box.current.setSelectionRange(r.start, r.end);
  }, [text]);

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Up in an empty box edits one's last message here.
    if (e.key === "ArrowUp" && !text && !e.shiftKey && !e.altKey && !e.metaKey && !e.ctrlKey && choices.length === 0) {
      const st = session.store.getSnapshot();
      const cs = st.channels.get(channel);
      const list = threadRoot ? sortedReplies(st, threadRoot) : cs ? sortedMessages(cs) : [];
      const mine = list.filter((m) => m.author.id === st.me?.id && m.class !== "ephemeral" && !m.payload_schema && !m.recalled_at).at(-1);
      // While one's newest is still on its way, an older one is not "last".
      const sending = [...st.pending.values()].some((p) => p.status === "sending" && p.channelID === channel && (p.threadRootID ?? null) === (threadRoot ?? null));
      if (mine && !sending) {
        e.preventDefault();
        dispatchEvent(new CustomEvent("lantern:edit", { detail: mine.id }));
        return;
      }
    }
    if ((e.metaKey || e.ctrlKey) && !e.altKey) {
      const k = e.key.toLowerCase();
      const f: Format | undefined = k === "b" && !e.shiftKey ? "bold" : k === "i" && !e.shiftKey ? "italic" : k === "x" && e.shiftKey ? "strike" : undefined;
      if (f) {
        e.preventDefault();
        format(f);
        return;
      }
    }
    if (choices.length > 0 && open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => (a + (e.key === "ArrowDown" ? 1 : choices.length - 1)) % choices.length);
        return;
      }
      if ((e.key === "Enter" || e.key === "Tab") && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        pick(choices[Math.min(active, choices.length - 1)]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setDismissed(open.start);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  }

  const ready = !uploading && (text.trim() !== "" || files.length > 0);
  const here = (scheduled ?? []).filter((x) => x.channel_id === channel && (x.thread_root_id ?? null) === (threadRoot ?? null));
  return (
    <div className="composer">
      {error && <p className="error">{error}</p>}
      {here.length > 0 && (
        <p className="scheduled-note">
          <IconClock size={14} />
          <span>
            {here.some((x) => x.failed_at)
              ? tn(here.length, "{n} message scheduled here · one could not be sent", "{n} messages scheduled here · one could not be sent")
              : tn(here.length, "{n} message scheduled here, next {when}", "{n} messages scheduled here, next {when}", { when: whenLabel(new Date(here[0].send_at)) })}
          </span>
          <button className="link" onClick={() => navigate("/scheduled")}>
            {t("See all")}
          </button>
        </p>
      )}
      {agentsOut.length > 0 && (
        <p className="ephemeral outside-note agents-note" role="alert">
          <span>
            {dmChannel
              ? tn(
                  agentsOut.length,
                  "{names} isn't in this conversation, so won't see this. To include it, start a new conversation with it.",
                  "{names} aren't in this conversation, so won't see this. To include them, start a new conversation with them.",
                  { names: list(agentsOut.map((id) => session.name(id))) },
                )
              : tn(agentsOut.length, "{names} isn't in this channel. Add it? It will be able to read this channel.", "{names} aren't in this channel. Add them? They will be able to read this channel.", {
                  names: list(agentsOut.map((id) => session.name(id))),
                })}
          </span>
          {!dmChannel && (
            <button className="small" onClick={() => void send("add")}>
              {t("Add and send")}
            </button>
          )}
          <button className="small" onClick={() => void send("without")}>
            {t("Send anyway")}
          </button>
          <button onClick={() => setAgentsOut([])} aria-label={t("Dismiss")}>
            <IconClose size={14} />
          </button>
        </p>
      )}
      {outside.length > 0 && (
        <p className="ephemeral outside-note" role="status">
          <span>
            {tn(outside.length, "{names} is not in this channel, so was not notified.", "{names} are not in this channel, so were not notified.", {
              names: list(outside.map((id) => session.name(id))),
            })}
          </span>
          <button className="small" onClick={() => void addOutside()}>
            {t("Add them")}
          </button>
          <button onClick={() => setOutside([])} aria-label={t("Dismiss")}>
            <IconClose size={14} />
          </button>
        </p>
      )}
      {ephemeral && (
        <p className="ephemeral" role="status">
          <span>{t("Only you can see this:")}</span> {ephemeral}
          <button onClick={() => setEphemeral("")} aria-label={t("Dismiss")}>
            <IconClose size={14} />
          </button>
        </p>
      )}
      {suggestions.length > 0 && (
        <ul className="command-suggestions" role="listbox" aria-label={t("Commands")}>
          {suggestions.map((c) => (
            <li key={c.command}>
              <button onClick={() => setText(c.command + " ")}>
                <strong>{c.command}</strong> {c.usage_hint} <span>{c.description || c.app_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {emojis.length > 0 && (
        <ul className="command-suggestions mention-suggestions emoji-suggestions" role="listbox" aria-label={t("Emoji")}>
          {emojis.map((e, i) => (
            <li key={e.name} role="option" aria-selected={i === Math.min(active, choices.length - 1)}>
              <button className={i === Math.min(active, choices.length - 1) ? "active" : ""} onMouseDown={(ev) => ev.preventDefault()} onClick={() => pick(e.name)}>
                <span className="emoji-suggestion-glyph" aria-hidden>
                  {isCustom(e.name) ? <Emoji name={e.name} /> : e.glyph}
                </span>
                :{e.name}:
              </button>
            </li>
          ))}
        </ul>
      )}
      {mq && choices.length > 0 && (
        <ul className="command-suggestions mention-suggestions" role="listbox" aria-label={t("People")}>
          {people.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === Math.min(active, choices.length - 1)}>
              <button
                className={i === Math.min(active, choices.length - 1) ? "active" : ""}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(p.handle)}
              >
                <Avatar id={p.id} name={p.display_name || p.handle} size={22} />
                <strong>{p.display_name || p.handle}</strong> @{p.handle}
                {p.kind === "agent" && <span> · {t("agent")}</span>}
                {!p.member && <span> · {t("not in this channel")}</span>}
              </button>
            </li>
          ))}
          {groups.map((g, j) => {
            const i = people.length + j;
            const on = i === Math.min(active, choices.length - 1);
            return (
              <li key={g.id} role="option" aria-selected={on}>
                <button className={on ? "active" : ""} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(g.handle)}>
                  <span className="group-icon" aria-hidden>
                    👥
                  </span>
                  <strong>{g.name}</strong> @{g.handle}
                  <span>
                    {" "}
                    · {t("group of {n}", { n: g.member_ids.length })}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div
        className={`composer-box ${dropping ? "dropping" : ""}`}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("Files")) {
            e.preventDefault();
            setDropping(true);
          }
        }}
        onDragLeave={(e) => e.currentTarget.contains(e.relatedTarget as Node) || setDropping(false)}
        onDrop={onDrop}
      >
        {files.length > 0 && (
          <ul className="chosen">
            {files.map((f, i) => (
              <li key={i}>
                <span className="file-name">{f.name}</span>
                <span className="size">{f.size < 1024 * 1024 ? `${Math.max(1, Math.round(f.size / 1024))} KB` : `${(f.size / 1024 / 1024).toFixed(1)} MB`}</span>
                <button onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={t("Remove {name}", { name: f.name })}>
                  <IconClose size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <input
          ref={picker}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <textarea
          ref={box}
          value={text}
          placeholder={placeholder}
          aria-label={placeholder}
          rows={1}
          onChange={(e) => {
            setText(e.target.value);
            setCaret(e.target.selectionStart);
            setActive(0);
            session.engine.typing(channel);
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
          onKeyDown={onKey}
          onPaste={(e) => {
            if (e.clipboardData.files.length > 0) {
              e.preventDefault();
              addFiles(e.clipboardData.files);
            }
          }}
        />
        <div className="composer-toolbar">
          <button className="tool" onClick={() => picker.current?.click()} aria-label={t("Attach files")} title={t("Attach files")}>
            <IconAttach />
          </button>
          <button
            className="tool"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => setEmojiAt(emojiAt ? null : e.currentTarget.getBoundingClientRect())}
            aria-label={t("Emoji")}
            title={t("Emoji")}
          >
            <IconReaction />
          </button>
          {emojiAt && (
            <EmojiPicker
              anchor={emojiAt}
              onClose={() => setEmojiAt(null)}
              onPick={(name) => {
                const el = box.current;
                const a = el?.selectionStart ?? text.length;
                const b = el?.selectionEnd ?? text.length;
                const g = glyph(name);
                selectAfter.current = { start: a + g.length, end: a + g.length };
                setText(text.slice(0, a) + g + text.slice(b));
              }}
            />
          )}
          {canWrite && (
            <label className={`tool write-help ${helping ? "busy" : ""}`} title={t("Writing help")}>
              <IconSparkle />
              <select aria-label={t("Writing help")} value="" disabled={helping} onChange={(e) => void help(e.target.value)}>
                <option value="">{helping ? t("Writing…") : t("Writing help")}</option>
                <option value="draft">{t("Draft a reply")}</option>
                <option value="clearer" disabled={!text.trim()}>
                  {t("Rewrite clearer")}
                </option>
                <option value="shorter" disabled={!text.trim()}>
                  {t("Make shorter")}
                </option>
                <option value="formal" disabled={!text.trim()}>
                  {t("Make more formal")}
                </option>
                <option value="translate" disabled={!text.trim()}>
                  {t("Translate…")}
                </option>
              </select>
            </label>
          )}
          <span className="format" role="group" aria-label={t("Formatting")}>
            {formats().map(([f, label, glyph, extra]) => (
              <button key={f} className={`tool ${extra ? "extra" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={() => format(f)} aria-label={label} title={label}>
                {glyph}
              </button>
            ))}
            <button className="tool" onMouseDown={(e) => e.preventDefault()} onClick={startLink} aria-label={t("Link")} title={t("Link")}>
              <IconLink />
            </button>
          </span>
          {threadRoot && (
            <label className="also-in-channel">
              <input type="checkbox" checked={alsoInChannel} onChange={(e) => setAlsoInChannel(e.target.checked)} />
              {dmChannel ? t("Also send as a direct message") : t("Also send to {channel}", { channel: session.channelName(channel) })}
            </label>
          )}
          <span className="composer-hint">
            {uploading
              ? t("Uploading…")
              : helping
                ? t("Writing…")
                : tx("{enter} to send · {shift}+{enter} for a new line", { enter: <kbd>Enter</kbd>, shift: <kbd>Shift</kbd> })}
          </span>
          <button
            className="tool schedule"
            onClick={(e) => setScheduling(e.currentTarget.getBoundingClientRect())}
            disabled={!text.trim() || files.length > 0 || text.trim().startsWith("/")}
            aria-label={t("Schedule message")}
            title={files.length > 0 ? t("Messages with files can't be scheduled yet") : t("Schedule for later")}
          >
            <IconClock />
          </button>
          <button className={`send ${ready ? "ready" : ""}`} onClick={() => void send()} disabled={!ready} aria-label={t("Send")} title={t("Send")}>
            <IconSend />
          </button>
          {linking && (
            <LinkSheet
              label={linking.label}
              onClose={() => setLinking(null)}
              onAdd={(label, url) => {
                const r = insertLink(text, linking.start, linking.end, label, url);
                selectAfter.current = r;
                setText(r.text);
                setLinking(null);
              }}
            />
          )}
          {asking !== null && (
            <Suspense fallback={null}>
              <NewTaskSheet channel={channel} threadRoot={threadRoot} typed={asking} onClose={() => setAsking(null)} />
            </Suspense>
          )}
          {scheduling && (
            <TimePicker
              anchor={scheduling}
              label={t("Send later")}
              title={t("Schedule message")}
              action={t("Schedule")}
              onClose={() => setScheduling(null)}
              onPick={(at) => void schedule(at)}
            />
          )}
        </div>
        {dropping && <div className="drop-hint">{t("Drop to attach")}</div>}
      </div>
    </div>
  );
}

// LinkSheet asks for a link's address, and its text, which starts as what
// was selected.
function LinkSheet({ label, onAdd, onClose }: { label: string; onAdd: (label: string, url: string) => void; onClose: () => void }) {
  const [text, setText] = useState(label);
  const [url, setURL] = useState("");
  const href = /^(https?:|mailto:)/i.test(url.trim()) ? url.trim() : url.trim() && `https://${url.trim()}`;
  return (
    <Sheet title={t("Add a link")} onClose={onClose} className="link-sheet">
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (href) onAdd(text, href);
        }}
      >
        <label>
          {t("Text")}
          <input value={text} onChange={(e) => setText(e.target.value)} autoFocus={!label} />
        </label>
        <label>
          {t("Link")}
          <input value={url} onChange={(e) => setURL(e.target.value)} placeholder="https://" inputMode="url" spellCheck={false} autoFocus={!!label} />
        </label>
        <div className="actions">
          <button type="button" className="secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button type="submit" disabled={!href}>
            {t("Add")}
          </button>
        </div>
      </Form>
    </Sheet>
  );
}

// mentionedIn is who a message's text mentions, each once.
function mentionedIn(text: string): string[] {
  return [...new Set([...text.matchAll(/<@([0-9a-f-]{36})>/g)].map((m) => m[1]))];
}
